import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { SimulatorAppliedFilters, SimulatorFilterCategory } from './SimulatorFilterPanel';
import type { SimulatorFilterScopeItem } from './simulatorFilter.helpers';
import type { useApplySimulatorFilters } from './useApplySimulatorFilters';

const SEARCH_DEBOUNCE_MS = 400;

type ApplySimulatorFilters = ReturnType<typeof useApplySimulatorFilters>['applySimulatorFilters'];

type UseSimulatorToolbarSearchArgs = {
    projectId?: number | string;
    scenarioId?: number;
    scenarioTabId: number | string | null;
    searchInputId?: string;
    filterCategories: SimulatorFilterCategory[];
    appliedScope?: SimulatorFilterScopeItem[];
    appliedFilters: SimulatorAppliedFilters;
    applySimulatorFilters: ApplySimulatorFilters;
};

export function useSimulatorToolbarSearch({
    projectId,
    scenarioId,
    scenarioTabId,
    searchInputId,
    filterCategories,
    appliedScope,
    appliedFilters,
    applySimulatorFilters,
}: UseSimulatorToolbarSearchArgs) {
    const [searchInput, setSearchInput] = useState('');
    const [searchKeyword, setSearchKeyword] = useState('');
    const skipSearchPostRef = useRef(true);
    const appliedFiltersRef = useRef(appliedFilters);

    appliedFiltersRef.current = appliedFilters;

    const debouncedSetKeyword = useMemo(
        () =>
            debounce((value: string) => {
                setSearchKeyword(value.trim());
            }, SEARCH_DEBOUNCE_MS),
        [],
    );

    useEffect(() => () => debouncedSetKeyword.cancel(), [debouncedSetKeyword]);

    useEffect(() => {
        setSearchInput('');
        setSearchKeyword('');
        skipSearchPostRef.current = true;
    }, [scenarioId, scenarioTabId]);

    const handleSearchChange = useCallback(
        (value: string) => {
            setSearchInput(value);
            if (value === '') {
                debouncedSetKeyword.cancel();
                setSearchKeyword('');
                return;
            }
            debouncedSetKeyword(value);
        },
        [debouncedSetKeyword],
    );

    const handleSearchSubmit = useCallback(
        (override?: string) => {
            const nextValue = override !== undefined ? override : searchInput;
            debouncedSetKeyword.cancel();
            setSearchInput(nextValue);
            setSearchKeyword(nextValue.trim());
        },
        [debouncedSetKeyword, searchInput],
    );

    useEffect(() => {
        if (skipSearchPostRef.current) {
            skipSearchPostRef.current = false;
            return;
        }

        if (!projectId || !scenarioId || scenarioTabId == null || !searchInputId) {
            return;
        }

        void applySimulatorFilters({
            projectId,
            scenarioId,
            scenarioTabId,
            filterCategories,
            appliedScope,
            appliedFilters: appliedFiltersRef.current,
            searchInput: searchKeyword ? { id: searchInputId, keyword: searchKeyword } : undefined,
        });
    }, [
        appliedScope,
        applySimulatorFilters,
        filterCategories,
        projectId,
        scenarioId,
        scenarioTabId,
        searchInputId,
        searchKeyword,
    ]);

    return {
        searchInput,
        searchKeyword,
        handleSearchChange,
        handleSearchSubmit,
    };
}
